import {
  callApiGet,
  callApiPatch,
  callApiPost,
  constructApiUrl,
  createParams,
  validateParams,
  getChangedParams,
  getPolicyID,
  getRequestPreparePurchase,
} from './common';
import { createErrorBody } from '../error';
import { createContent, updateContent, prepareContent } from '../../actions/Api';
import { getPackagesWithCoverage } from '../package/packageUtils';

const type = 'content';

/**
 * Determines if the state allows us to run updates.
 * @param  {object} state The current state.
 * @return {boolean}      If we can update or not.
 */
const shouldUpdate = (state) => {
  return state.isUpdatingContent ? state.isUpdatingContent : true;
};

/**
 * Call the API using GET.
 * @param  {string}   service  The service to call.
 * @param  {Object}   params   The params for the service.
 * @param  {Function} dispatch The dispatch function.
 * @param  {boolean} externalService
 * @param  {boolean} useToken
 * @return {Promise}           The Promise.
 */
const callContentApiGet = (service, params, dispatch, externalService = false, useToken = true) => {
  const url = constructApiUrl(type, service);
  return callApiGet(url, params, dispatch, externalService, useToken);
};

/**
 * Call the API using POST.
 * @param  {string} service The service to call.
 * @param  {Object} params  The params for the service.
 * @param  {function} dispatch
 * @return {Promise}        The Promise.
 */
const callContentApiPost = (service, params, dispatch) => {
  const url = constructApiUrl(type, service);
  return callApiPost(url, params, dispatch);
};

/**
 * Call the API using PATCH.
 * @param  {string} service The service to call.
 * @param  {Object} params  The params for the service.
 * @param  {function} dispatch
 * @return {Promise}        The Promise.
 */
const callContentApiPatch = (service, params, dispatch) => {
  const url = constructApiUrl(type, service);
  return callApiPatch(url, params, dispatch);
};

/**
 * Get Excess Amount for Content.
 *
 * @param {Object} formValues
 * @param {function} dispatch
 * @return {Promise}
 */
export const getContentExcessAmount = (formValues, dispatch) => {
  const { user: { customerID } } = formValues;
  const policyID = getPolicyID(formValues, type, true);
  const service = `getExcessAmount/${customerID}/${policyID}`;
  return callContentApiGet(service, {}, dispatch, false, false);
};

/**
 * Dispatches the create action.
 * @param  {string} service The service to use.
 * @param  {Object} parameters The params to call the service with.
 * @return {function} Dispatch function.
 */
const callCreateContent = (service, parameters) => {
  return dispatch => {
    const params = parameters;
    let id = null;
    if (Object.hasOwnProperty.call(params, 'packageId')) {
      const { packageId } = params;
      id = packageId;
      delete params.packageId;
    }
    return callContentApiPost(service, params, dispatch).then(data => {
      const newData = data;
      if (id) {
        newData.packageId = id;
      }
      return newData;
    });
  };
};

/**
 * Call the create  service.
 * @param  {Object} params The params to call the service with.
 * @param  {Object} formValues
 * @return {Promise}     The promise of the service.
 */
export const requestCreateContent = (params, formValues) => {
  return (dispatch, getState) => {
    const state = getState();
    if (shouldUpdate(getState())) {
      const mappedParams = params ? createParams(params, formValues, state) : {};
      // Ensure that we have the correct paramters. Fail if not.
      const errors = params ? validateParams(mappedParams, params) : [];
      if (errors.length > 0) {
        return Promise.reject(createErrorBody('Missing parameters', errors));
      }
      const service = '';
      const serviceCalls = [];
      const packageInfo = getPackagesWithCoverage(state);

      dispatch(createContent());

      if (packageInfo.length > 0) {
        packageInfo.forEach(item => {
          serviceCalls.push(dispatch(callCreateContent(service, { ...mappedParams, ...item })));
        });
        return Promise.all(serviceCalls).then(data => {
          let response = {};
          if (data.length > 1) {
            data.forEach(element => {
              response[element.packageId] = element;
            });
          } else if (data.length === 1) {
            [response] = data;
          }
          return response;
        });
      }

      return dispatch(callCreateContent(service, mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the update action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callUpdateContent = (service, params) => {
  return dispatch => {
    dispatch(updateContent());
    return callContentApiPatch(service, params, dispatch);
  };
};

/**
 * Call the update service.
 * @param  {array}  params     The params to call the service with.
 * @param  {Object} formValues Values from the form that might not yet be set in the state.
 * @return {Promise}           The promise of the service.
 */
export const requestUpdateContent = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const state = getState();
      let mappedParams = params ? createParams(params, formValues, state) : {};
      const policyID = getPolicyID(state.userData, type);
      if (state.userData.content) {
        // Find the changed values and pass those along.
        const additionalParams = { policyID };
        mappedParams = getChangedParams(mappedParams, state.userData.content.usedParams, ['policyID'], additionalParams);
      }
      const service = `${policyID}`;
      return dispatch(callUpdateContent(service, mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the prepare action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callPrepareContent = (service, params) => {
  return dispatch => {
    dispatch(prepareContent());
    return callContentApiPatch(service, params, dispatch);
  };
};

/**
 * Call the prepare purchase service.
 * @param  {array}  params     The params to call the service with.
 * @param  {Object} formValues Values from the form that might not yet be set in the state.
 * @return {Promise}     The promise of the service.
 */
export const requestPrepareContent = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const prepare = getRequestPreparePurchase(type, getState(), formValues, params);
      return dispatch(callPrepareContent(prepare.service, prepare.mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};



// WEBPACK FOOTER //
// ./src/utilities/api/content.js